import { inject, Injector, runInInjectionContext } from "@angular/core";
import { CanActivateFn, Routes } from "@angular/router";
import { TryPrependAFDRouteMatchGuard } from "src/app/guards/try-prepend-afd-route-match-guard";
import {
  StandardLoggedInGuard,
  StandardLogoutGuard,
} from "src/standard/auth/auth";
import { SecurityActivateGuard } from "src/standard/security/security-activate.guard";
import workspaceRoutes from "./workspace/routes";
import { DownloadsGuard } from "src/app/guards/downloads.guard";
import { firstValueFrom, isObservable } from "rxjs";

const TypedStandardLoggedInGuard: CanActivateFn = StandardLoggedInGuard;

const TryLoggedInGuard: CanActivateFn = async (next, state) => {
  const injector = inject(Injector);

  const res = await TypedStandardLoggedInGuard(next, state);
  if (res === true) {
    // log in
    await runInInjectionContext(injector, async () => {
      await SecurityActivateGuard(next, state);
    });
  }

  if (isObservable(res)) {
    return await firstValueFrom(res);
  } else {
    return res;
  }
};

const routes: Routes = [
  {
    path: "",
    redirectTo: "/ws",
    pathMatch: "full",
  },
  {
    path: "oauth2-redirect",
    loadComponent: () =>
      import(
        "src/standard/pages/auth/oauth2-redirect/oauth2-redirect.component"
      ),
  },
  {
    path: "maintenance",
    loadComponent: () =>
      import(
        "src/standard/pages/error-pages/maintenance/maintenance.component"
      ),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "login",
    loadComponent: () => import("src/standard/pages/login.component"),
    canActivate: [StandardLoggedInGuard],
  },
  {
    path: "logout",
    loadComponent: () =>
      import("src/standard/standard-empty/standard-empty.component"),
    canActivate: [StandardLogoutGuard],
  },
  {
    path: "change-locale",
    loadComponent: () => import("src/standard/pages/change-locale.component"),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "access-denied",
    loadComponent: () =>
      import(
        "src/standard/pages/error-pages/access-denied/access-denied.component"
      ),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "firm-not-onboarded",
    loadComponent: () =>
      import(
        "src/standard/pages/error-pages/firm-not-onboarded/firm-not-onboarded.component"
      ),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "development",
    loadChildren: () => import("../standard/pages/development/routes"),
  },

  {
    path: "",
    canActivate: [StandardLoggedInGuard],
    children: [
      {
        path: "ws",
        children: workspaceRoutes,
      },
      {
        path: "downloads",
        loadChildren: () => import("./nav-content/download-list/routes"),
        canActivate: [DownloadsGuard],
      },
      {
        path: "design-system",
        loadChildren: () => import("../standard/pages/design-system/routes"),
      },
      // in cases where we're missing the afdRoute,
      // we can redirect to the path with the afdRoute included
      {
        path: "**",
        loadComponent: () =>
          import("src/standard/standard-empty/standard-empty.component"),
        canMatch: [TryPrependAFDRouteMatchGuard],
      },
      {
        path: "**",
        loadComponent: () =>
          import(
            "src/standard/pages/error-pages/page-not-found/page-not-found.component"
          ),
        canActivate: [TryLoggedInGuard],
      },
    ],
  },
];

export default routes;
